import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import {
  SegmentAuthAction,
  SegmentAuthError,
  SegmentAuthFailure,
  SegmentAuthResult,
  authenticateSegment,
  deleteSegmentInConnection,
  type SegmentAuthApiError,
  type SegmentAuthOutcome,
} from '@/services/db/segment-in-integration/mutations';
import { markSegmentConnected, releaseSegmentSetupGuideStep } from '@/services/db/segment-in-integration/setup-guide';
import { segmentInConnectionQueryKey } from '@/services/db/segment-in-integration/queries';
import { useAnalytics } from '@/services/analytics';
import { disabledProperties, enableProperties, verifyCompletedProperties, verifyStartedProperties } from './analytics';
import { deriveConnectionAlert, type SegmentAlertDescriptor } from './connection-alert';
import { ConnectionState, deriveConnectionState, oauthActionForState, stateFromErrors } from './connection-status';

/** The actions that turn a destination on, as opposed to verifying or removing one. */
/**
 * Whether these errors change what the destination is, as opposed to explaining
 * why this attempt could not be made. Only the former replaces the stored outcome.
 */
function describesDestination(errors: readonly SegmentAuthApiError[]): boolean {
  return stateFromErrors(errors) != null;
}

/**
 * Whether the attempt failed at the popup, before the API was reached.
 *
 * These say nothing about the destination — the reader closed the popup, Segment
 * no longer authorizes them, or we stopped waiting while they were still
 * consenting — so the status must stay as the last real attempt left it.
 */
function isPopupFailure(failure: SegmentAuthFailure): boolean {
  return failure === SegmentAuthFailure.Authorization || failure === SegmentAuthFailure.Timeout;
}

/** What the reader was shown, which is the property the verify event carries. */
function describeOutcome(outcome: SegmentAuthOutcome): string {
  if (outcome.enabled === false) return 'disabled';
  return outcome.state;
}

const CLEAR_FAILED_MESSAGE = "Your connection couldn't be cleared. Try again.";

const ENABLING_ACTIONS: readonly SegmentAuthAction[] = [
  SegmentAuthAction.Create,
  SegmentAuthAction.Default,
  SegmentAuthAction.Enable,
];

/** The analytics `track` as its owner declares it, so the event names stay checked. */
type TrackFn = ReturnType<typeof useAnalytics>['track'];

/** The events that fire when an attempt starts, on Ember's exact conditions. */
function trackAttemptStarted(track: TrackFn, action: SegmentAuthAction) {
  if (ENABLING_ACTIONS.includes(action)) {
    track('integration', 'integrationEnableStarted', enableProperties(action === SegmentAuthAction.Enable));
  }
  if (action === SegmentAuthAction.Check) {
    track('integration', 'integrationVerifyStarted', verifyStartedProperties());
  }
}

/**
 * Their completion halves. Verify reports on both outcomes, as in Ember, because
 * a verify that failed is exactly what the event exists to measure.
 */
function trackAttemptCompleted(track: TrackFn, action: SegmentAuthAction, status?: string) {
  if (ENABLING_ACTIONS.includes(action)) {
    track('integration', 'integrationEnabled', enableProperties(action === SegmentAuthAction.Enable));
  }
  if (action === SegmentAuthAction.Check) {
    track('integration', 'integrationVerifyCompleted', verifyCompletedProperties(status));
  }
}

/**
 * The onboarding step, claimed on a connect and released on a delete.
 *
 * Unawaited and best-effort, as in Ember: a guide write must not turn a
 * successful connect into a failure.
 */
function applySetupGuideSideEffects(workspaceId: number, action: SegmentAuthAction) {
  // Ember stamped the guide for every action that reached this point, verify
  // included — the write sat outside its `enabling` guard. An account whose only
  // successful action is a verify has still integrated.
  if (action !== SegmentAuthAction.Delete) void markSegmentConnected({ workspaceId });
  void releaseSegmentSetupGuideStep({ workspaceId, action });
}

type LatestFailure = {
  failure: SegmentAuthFailure;
  /** The action that failed, which names the control the banner tells them to retry with. */
  action: SegmentAuthAction;
  errors: readonly SegmentAuthApiError[];
};

/**
 * Drives the connector's actions and holds what the last one said.
 *
 * The outcome is kept on the screen rather than refetched into it: a closed OAuth
 * popup, or a destination that answered "already exists", says more about the
 * connection than the stored record does, and the record cannot express it.
 */
/**
 * What the last attempt said, and what that makes the connection.
 *
 * Held apart from the actions because it is the whole contract the screen reads:
 * the status, the banner, and whether anything is in flight all come from here.
 */
function useSegmentAttemptState(workspaceId: string, hasStoredConnection: boolean) {
  const [latestOutcome, setLatestOutcome] = useState<SegmentAuthOutcome | null>(null);
  const [latestFailure, setLatestFailure] = useState<LatestFailure | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  // The banner is dismissable, and every new attempt brings it back.
  const [isAlertDismissed, setIsAlertDismissed] = useState(true);
  // The credential the last attempt used, which tells a credential clash from a
  // workspace clash when the API reports a destination mismatch.
  const [attemptedSiteId, setAttemptedSiteId] = useState<string | null>(null);
  // Clearing has no outcome to describe, so its failure gets its own message.
  const [clearError, setClearError] = useState<string | null>(null);

  const state = deriveConnectionState({
    latestOutcome,
    latestFailure: latestFailure != null && isPopupFailure(latestFailure.failure) ? null : latestFailure,
    hasStoredConnection,
  });

  // The banner describes the last attempt, which is the failure when one did not
  // move the destination — those keep the outcome but still have something to say.
  const attempt: SegmentAuthOutcome | null =
    latestFailure != null
      ? { state: SegmentAuthResult.Errored, action: latestFailure.action, errors: [...latestFailure.errors] }
      : latestOutcome;

  const alert: SegmentAlertDescriptor | null =
    isAlertDismissed || attempt == null
      ? null
      : deriveConnectionAlert({
          outcome: attempt,
          failure: latestFailure?.failure ?? null,
          currentWorkspaceId: workspaceId,
          currentSiteId: attemptedSiteId,
        });

  return {
    state,
    alert,
    isRunning,
    latestFailure,
    clearError,
    setLatestOutcome,
    setLatestFailure,
    setIsRunning,
    setIsAlertDismissed,
    setAttemptedSiteId,
    setClearError,
  };
}

/**
 * Drives the connector's actions and holds what the last one said.
 *
 * The outcome is kept on the screen rather than refetched into it: a closed OAuth
 * popup, or a destination that answered "already exists", says more about the
 * connection than the stored record does, and the record cannot express it.
 */
export function useSegmentConnection(workspaceId: string, hasStoredConnection: boolean) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();

  const {
    state,
    alert,
    isRunning,
    latestFailure,
    clearError,
    setLatestOutcome,
    setLatestFailure,
    setIsRunning,
    setIsAlertDismissed,
    setAttemptedSiteId,
    setClearError,
  } = useSegmentAttemptState(workspaceId, hasStoredConnection);

  const refreshConnection = useCallback(() => {
    // Invalidating the workspace's integration namespace also moves the directory
    // badge, which reads the same endpoint on its own slot.
    void queryClient.invalidateQueries({ queryKey: segmentInConnectionQueryKey(Number(workspaceId)) });
  }, [queryClient, workspaceId]);

  const run = useCallback(
    async (credential: ApiCredential, action: SegmentAuthAction) => {
      setIsAlertDismissed(true);
      setAttemptedSiteId(credential.site_id);
      setIsRunning(true);

      trackAttemptStarted(track, action);

      try {
        const outcome = await authenticateSegment({
          workspaceId: Number(workspaceId),
          siteId: credential.site_id,
          apiKey: credential.api_key,
          action,
        });

        setLatestOutcome(outcome);
        setLatestFailure(null);
        setIsAlertDismissed(false);

        trackAttemptCompleted(track, action, describeOutcome(outcome));

        applySetupGuideSideEffects(Number(workspaceId), action);

        refreshConnection();
        return outcome;
      } catch (error) {
        // A failure that never reached the API says nothing about the destination:
        // the reader closed the popup, or Segment no longer authorizes them. Ember
        // left the status untouched for exactly this case, so the screen keeps
        // offering the same action rather than falling back to "not connected".
        if (error instanceof SegmentAuthError) {
          setLatestFailure({ failure: error.failure, action, errors: error.errors });
          // Only replace the outcome when the errors say something about the
          // destination. A mismatch does not: the destination is still there and
          // still in whatever state the last real attempt left it, so overwriting
          // would drop the status to unknown and take the Options menu and the
          // clear escape hatch with it. Ember left the response untouched here.
          // A failure at the popup never reached the API, so it says nothing about
          // the destination and must not move the status — Ember's own test names
          // this ("Keeps the same status when the oauth popup is closed"). A wait
          // that ran out is the same: the reader may still have been consenting.
          if (!isPopupFailure(error.failure) && describesDestination(error.errors)) {
            setLatestOutcome({ state: SegmentAuthResult.Errored, action, errors: [...error.errors] });
          }
          setIsAlertDismissed(false);
          if (action === SegmentAuthAction.Check) {
            track('integration', 'integrationVerifyCompleted', verifyCompletedProperties(error.failure));
          }
        }
        // The connection may have moved even on a failure — a delete that 404s
        // means it is already gone — so the record is re-read either way.
        refreshConnection();
        throw error;
      } finally {
        setIsRunning(false);
      }
    },
    [refreshConnection, track, workspaceId],
  );

  /**
   * The exposed actions settle rather than reject.
   *
   * A failure is already recorded in the state the screen renders — the banner and
   * the connection status both derive from it — so rejecting as well would leave
   * every call site writing a catch whose only job is to discard, and a missed one
   * becomes an unhandled rejection rather than a visible error.
   *
   * The completion event fires only on success, as in Ember, where the throw
   * skipped past it.
   */
  const runAndSettle = useCallback(
    async (credential: ApiCredential, action: SegmentAuthAction, onSuccess?: () => void) => {
      try {
        const outcome = await run(credential, action);
        onSuccess?.();
        return outcome;
      } catch {
        return null;
      }
    },
    [run],
  );

  const connect = useCallback(
    (credential: ApiCredential) => runAndSettle(credential, oauthActionForState(state)),
    [runAndSettle, state],
  );

  const disable = useCallback(
    (credential: ApiCredential) =>
      runAndSettle(credential, SegmentAuthAction.Disable, () => {
        track('integration', 'integrationDisabled', disabledProperties('Disabled'));
      }),
    [runAndSettle, track],
  );

  const remove = useCallback(
    (credential: ApiCredential) =>
      runAndSettle(credential, SegmentAuthAction.Delete, () => {
        track('integration', 'integrationDisabled', disabledProperties('Deleted'));
      }),
    [runAndSettle, track],
  );

  /**
   * Forgets the stored connection without touching Segment.
   *
   * The only action that does not re-authorize: it exists for a connection the
   * OAuth actions can no longer reach, so requiring one would be circular. It
   * clears the remembered attempt too, since that attempt described a connection
   * that is now gone.
   */
  const clear = useCallback(async () => {
    setIsRunning(true);
    try {
      await deleteSegmentInConnection({ workspaceId: Number(workspaceId) });
      setLatestOutcome(null);
      setLatestFailure(null);
      setIsAlertDismissed(true);
      setClearError(null);
      refreshConnection();
    } catch {
      // Ember's service reported this rather than rethrowing, so the reader is
      // told the clear failed instead of the modal closing on a no-op.
      setClearError(CLEAR_FAILED_MESSAGE);
    } finally {
      setIsRunning(false);
    }
  }, [refreshConnection, setClearError, workspaceId]);

  return {
    state,
    alert,
    clearError,
    dismissClearError: () => setClearError(null),
    dismissAlert: () => setIsAlertDismissed(true),
    isRunning,
    isConnected: state === ConnectionState.Enabled,
    latestFailure,
    connect,
    disable,
    remove,
    clear,
  };
}
